import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Briefcase, TrendingUp, Users, Network, Globe, BookOpen, Layers } from 'lucide-react';
import { AdminGroupKey, adminGroups, getGroupForPath } from '../../config/adminGroups';
import {
  Bars3Icon,
  XMarkIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  HomeIcon,
  SparklesIcon,
    BanknotesIcon,
    DocumentIcon,
  InboxArrowDownIcon
} from '@heroicons/react/24/outline';
import { SparklesIcon as SparklesSolidIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../../contexts/AuthContext';
import { isAdminRole } from '../../utils/authRoles';
import { useChatContext } from '../../contexts/ChatContext';
import { NotificationBell } from '../admin/NotificationBell';
import { useHideOnScroll } from '../../hooks/useHideOnScroll';
import UserAvatar from './UserAvatar';
import {
  ALFRED_AI_GUIDE_PATH,
  getApprovedClientHomePath,
} from '../../constants/alfredAiGuideAccess';
import { VOLOCEAN_ASSETS, VOLOCEAN_WORDMARK_IMG_CLASS } from '../../types/volocean.types';

interface DropdownSection {
  name?: string;
  items: NavigationItem[];
}

interface NavigationItem {
  name: string;
  href?: string;
  icon?: React.ComponentType<any>;
  isDropdown?: boolean;
  sections?: DropdownSection[];
  disabled?: boolean;
  disabledReason?: string;
  groupKey?: AdminGroupKey;
  /** Desktop: render on the right side of the middle nav row (same band as primary links), not with utility icons */
  trailing?: boolean;
}

const ADMIN_GROUP_ICONS: Record<AdminGroupKey, React.ComponentType<any>> = {
  dashboard: HomeIcon,
  crm: Users,
  sales: Briefcase,
  customers: Users,
  delivery: Layers,
  partnerships: Network,
  pages: Globe,
};

const ADMIN_TAB_ICONS: Record<string, React.ComponentType<any>> = {
  leads: InboxArrowDownIcon,
  pipeline: Briefcase,
  clients: Users,
  investors: TrendingUp,
  delivery: Layers,
  modular: Briefcase,
  partners: Network,
  syndicates: TrendingUp,
  publicPages: Globe,
  manual: BookOpen,
  campaigns: InboxArrowDownIcon,
};

const EASING: [number, number, number, number] = [0.25, 0.46, 0.45, 0.94];

const UTILITY_SCALE = {
  rest: { scale: 1 },
  hover: { scale: 1.04 },
};

const SPARKLE_OUT = {
  rest: { opacity: 1, scale: 1, rotate: 0 },
  hover: { opacity: 0, scale: 0.85, rotate: -25 },
};

const SPARKLE_IN = {
  rest: { opacity: 0, scale: 0.85, rotate: 25 },
  hover: { opacity: 1, scale: 1, rotate: 0 },
};

const GEAR_ROTATE = {
  rest: { rotate: 0 },
  hover: { rotate: 90 },
};

const ICON_MOTION_TRANSITION = { duration: 0.15 };

interface NavigationProps {
  spacerSize?: 'none' | 'sm' | 'md' | 'lg';
}

const Navigation: React.FC<NavigationProps> = ({ spacerSize = 'md' }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isBellOpen, setIsBellOpen] = useState(false);
  const { user, logout } = useAuth();
  const { toggleChat } = useChatContext();
  const location = useLocation();
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const iconHover = reduceMotion ? undefined : 'hover';
  const frozen = isMobileMenuOpen || isUserMenuOpen || isBellOpen;
  const { hidden: navHidden, scrolled, reveal } = useHideOnScroll({
    frozen,
    resetKey: location.pathname,
  });

  // Close menus on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
    setIsBellOpen(false);
  }, [location.pathname, location.search]);

  // ESC key closes mobile menu
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
    };
    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden'; // Prevent background scroll
      return () => {
        document.removeEventListener('keydown', handleEsc);
        document.body.style.overflow = '';
      };
    }
  }, [isMobileMenuOpen]);

  // Close user dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setIsUserMenuOpen(false);
    if (isUserMenuOpen) {
      document.addEventListener('click', handleClickOutside, { passive: true } as AddEventListenerOptions);
      return () => document.removeEventListener('click', handleClickOutside as EventListener);
    }
  }, [isUserMenuOpen]);

  // Nav visibility is controlled by LayoutWrapper via config/layout.ts

  const isActivePage = (href: string) => {
    if (href.includes('?')) {
      return `${location.pathname}${location.search}`.startsWith(href);
    }
    if (href === '/') return location.pathname === '/';
    // Exact match so sibling routes do not double-light (Dashboard, Delivery vs Modular).
    if (href === '/admin') return location.pathname === '/admin';
    if (href === '/admin/delivery') return location.pathname === '/admin/delivery';
    return location.pathname.startsWith(href);
  };

  const isNavItemActive = (item: NavigationItem) => {
    if (item.disabled) return false;
    if (item.groupKey) {
      return getGroupForPath(location.pathname)?.key === item.groupKey;
    }
    return Boolean(item.href && isActivePage(item.href));
  };

  // Minimal public landing: no nav items
  const publicNav: NavigationItem[] = [];

  const clientNavFull: NavigationItem[] = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    {
      name: 'Growth Engine',
      href: '/growth-engine',
      icon: SparklesIcon,
    },
    {
      name: 'Capital Raising',
      href: '/capital-raising',
      icon: BanknotesIcon,
    },
    { name: 'Profile', href: '/profile', icon: UserCircleIcon },
    { name: 'Documents', href: '/documents', icon: DocumentIcon },
  ];

  /** Alfred guide-only clients: single destination in nav */
  const clientNavAlfredMinimal: NavigationItem[] = [
    { name: 'Target customer guide', href: ALFRED_AI_GUIDE_PATH, icon: HomeIcon },
  ];

  const clientNavItems =
    user?.role === 'client' && getApprovedClientHomePath(user.email) !== '/dashboard'
      ? clientNavAlfredMinimal
      : clientNavFull;

  const adminNav: NavigationItem[] = adminGroups.map((group) => ({
    name: group.label,
    href: group.href,
    icon: ADMIN_GROUP_ICONS[group.key],
    disabled: group.disabled,
    disabledReason: group.disabledReason,
    groupKey: group.key,
  }));

  const navItems = user ? (isAdminRole(user.role) ? adminNav : clientNavItems) : publicNav;
  const navPrimaryItems = navItems.filter((i) => !i.trailing);

  const brandHomePath =
    !user ? '/' : isAdminRole(user.role) ? '/admin' : user.role === 'client'
      ? getApprovedClientHomePath(user.email)
      : '/dashboard';
  const isNavItemDisabled = (item: NavigationItem) => item.disabled === true;

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch {}
  };

  // Safe conversion of createdAt to Date
  const getUserCreatedAt = (): Date | undefined => {
    if (!user?.createdAt) return undefined;
    
    // Handle Firestore Timestamp object (has toDate method)
    if (typeof (user.createdAt as any).toDate === 'function') {
      return (user.createdAt as any).toDate();
    }
    
    // Handle serialized Timestamp object (has seconds/nanoseconds but no methods)
    if ((user.createdAt as any).seconds) {
      return new Date((user.createdAt as any).seconds * 1000);
    }
    
    // Handle standard Date object or string
    return new Date(user.createdAt as any);
  };

  const actionIconButtonClass =
    'inline-flex items-center justify-center w-9 h-9 rounded-full border border-primary-500/10 text-primary-500 hover:text-accent-500 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500';

  return (
    <>
      <motion.header
        initial={false}
        animate={{ y: navHidden ? '-120%' : 0, opacity: 1 }}
        transition={{ duration: reduceMotion ? 0 : 0.18, ease: EASING }}
        onFocusCapture={reveal}
        className={[
          'fixed z-40 top-4 left-4 right-4 md:left-6 md:right-6',
          navHidden ? 'pointer-events-none' : '',
        ].join(' ')}
      >
        <nav
            className={[
              'relative mx-auto max-w-[1400px] rounded-lg transition-shadow duration-300 ease-out',
              'border',
              // Use fully opaque cream background to prevent underlying borders/shadows from showing through as a line
              scrolled
                ? 'bg-primary-50 border-primary-500/10 shadow-[0_8px_24px_var(--color-primary-alpha-12)]'
                : 'bg-primary-50 border-primary-500/5 shadow-[0_4px_18px_var(--color-primary-alpha-08)]',
            ].join(' ')}
          aria-label="Primary"
        >
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-[auto_1fr_auto] items-center h-16">
              {/* Brand */}
              <div className="flex items-center min-w-0">
                <Link
                  to={brandHomePath}
                  className="flex items-center shrink-0"
                  aria-label="VOLOCEAN Home"
                >
                  <img
                    src={VOLOCEAN_ASSETS.logoBlack}
                    alt="VOLOCEAN logo"
                    className={VOLOCEAN_WORDMARK_IMG_CLASS.navBar}
                    style={{ shapeRendering: 'geometricPrecision' }}
                    loading="eager"
                    decoding="async"
                  />
                </Link>
              </div>

              {/* Middle column: primary links (centered) */}
              {/* xl+: primary links (lg–xl was crowded with 7 admin labels — R-12) */}
              <div className="hidden xl:flex items-center justify-center min-w-0 min-h-0 w-full">
                <div className="flex items-center justify-center gap-2 min-w-0 flex-nowrap">
                  {navPrimaryItems.map((item) => {
                    const isActive = isNavItemActive(item);
                    return (
                      <div key={item.name} className="relative px-2 py-1.5">
                        {isNavItemDisabled(item) ? (
                          <div
                            className="inline-flex items-center gap-2 text-sm font-medium text-neutral-600 cursor-not-allowed"
                            aria-disabled="true"
                            title={item.disabledReason || 'Coming soon'}
                          >
                            {item.name}
                          </div>
                        ) : (
                          <Link
                            to={item.href || '#'}
                            className={[
                              'inline-flex items-center gap-2 text-sm font-medium transition-colors',
                              isActive
                              ? 'text-primary-500'
                              : 'text-primary-500/80 hover:text-primary-500'
                            ].join(' ')}
                            aria-current={isActive ? 'page' : undefined}
                          >
                            {item.name}
                          </Link>
                        )}
                        {!isNavItemDisabled(item) && isActive && (
                          <motion.div
                            className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full bg-primary-500"
                            initial={{ scaleX: 0 }}
                            animate={{ scaleX: 1 }}
                            transition={{ duration: 0.4, ease: EASING }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Utility actions */}
              <div className="flex items-center justify-end gap-2 sm:gap-3 min-w-0">
                {/* AI Chat Toggle */}
                {user && (
                  <motion.button
                    type="button"
                    onClick={toggleChat}
                    aria-label="Open AI Chat"
                    className={actionIconButtonClass}
                    initial="rest"
                    animate="rest"
                    whileHover={iconHover}
                    variants={UTILITY_SCALE}
                    transition={ICON_MOTION_TRANSITION}
                  >
                    <span className="relative inline-flex w-4 h-4 items-center justify-center">
                      <motion.span
                        className="absolute inset-0 inline-flex"
                        variants={SPARKLE_OUT}
                        transition={ICON_MOTION_TRANSITION}
                      >
                        <SparklesIcon className="w-4 h-4" aria-hidden="true" />
                      </motion.span>
                      <motion.span
                        className="absolute inset-0 inline-flex pointer-events-none"
                        variants={SPARKLE_IN}
                        transition={ICON_MOTION_TRANSITION}
                        aria-hidden="true"
                      >
                        <SparklesSolidIcon className="w-4 h-4" />
                      </motion.span>
                    </span>
                  </motion.button>
                )}

                {/* Notification Bell (all authenticated users) */}
                {user && (
                  <NotificationBell 
                    userId={user.uid} 
                    userRole={isAdminRole(user.role) ? 'admin' : 'client'} 
                    userCreatedAt={getUserCreatedAt()}
                    onOpenChange={setIsBellOpen}
                  />
                )}

                {/* Settings icon (desktop only - xl and above; hamburger below xl — R-12) */}
                {user && (
                  <motion.button
                    type="button"
                    onClick={() => navigate(isAdminRole(user.role) ? '/admin/settings' : '/settings')}
                    aria-label="Settings"
                    className={`hidden xl:inline-flex ${actionIconButtonClass}`}
                    initial="rest"
                    animate="rest"
                    whileHover={iconHover}
                    variants={UTILITY_SCALE}
                    transition={ICON_MOTION_TRANSITION}
                  >
                    <motion.span
                      className="inline-flex"
                      variants={GEAR_ROTATE}
                      transition={ICON_MOTION_TRANSITION}
                    >
                      <Cog6ToothIcon className="w-4 h-4" aria-hidden="true" />
                    </motion.span>
                  </motion.button>
                )}

                {/* User menu (desktop only - xl and above) */}
                {user && (
                  <div className="hidden xl:block relative">
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsUserMenuOpen(!isUserMenuOpen);
                      }}
                      type="button"
                      className="flex items-center justify-center rounded-full overflow-hidden w-9 h-9 border border-primary-500/10 text-primary-500 hover:text-accent-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 transition-colors duration-150"
                      aria-expanded={isUserMenuOpen}
                      aria-haspopup="true"
                      aria-label="Account menu"
                      initial="rest"
                      animate="rest"
                      whileHover={iconHover}
                      variants={UTILITY_SCALE}
                      transition={ICON_MOTION_TRANSITION}
                    >
                      <UserAvatar user={user} size="md" showBorder={false} className="!w-9 !h-9" />
                    </motion.button>

                    <AnimatePresence>
                      {isUserMenuOpen && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.98, y: -8 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.98, y: -8 }}
                          transition={{ duration: 0.2, ease: EASING }}
                          className="absolute right-0 top-full mt-2 w-56 backdrop-blur rounded-lg shadow-[0_8px_24px_var(--color-primary-alpha-12)] border bg-white border-primary-500/10"
                        >
                          <div className="p-2">
                            <button
                              onClick={() => {
                                setIsUserMenuOpen(false);
                                navigate('/settings');
                              }}
                               className="w-full flex items-center px-3 py-2 text-sm text-primary-500 rounded-lg transition-colors hover:bg-primary-500/5"
                            >
                              <UserCircleIcon className="w-5 h-5 mr-3" />
                              Account Settings
                            </button>
                            <button
                              onClick={() => {
                                setIsUserMenuOpen(false);
                                handleLogout();
                              }}
                               className="w-full flex items-center px-3 py-2 text-sm text-primary-500 rounded-lg transition-colors hover:bg-primary-500/5"
                            >
                              <ArrowRightOnRectangleIcon className="w-5 h-5 mr-3" />
                              Sign Out
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {/* Mobile menu toggle - Shows below xl (R-12 mid-width crowding) */}
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="xl:hidden p-3 rounded-lg text-primary-500/70 hover:text-primary-500 hover:bg-primary-100/50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-accent-500 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                  aria-label="Toggle menu"
                >
                  <AnimatePresence mode="wait" initial={false}>
                    {isMobileMenuOpen ? (
                      <motion.span
                        key="x"
                        initial={{ opacity: 0, rotate: -90 }}
                        animate={{ opacity: 1, rotate: 0 }}
                        exit={{ opacity: 0, rotate: 90 }}
                        transition={{ duration: 0.2, ease: EASING }}
                        className="inline-flex"
                      >
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </motion.span>
                    ) : (
                      <motion.span
                        key="bars"
                        initial={{ opacity: 0, rotate: 90 }}
                        animate={{ opacity: 1, rotate: 0 }}
                        exit={{ opacity: 0, rotate: -90 }}
                        transition={{ duration: 0.2, ease: EASING }}
                        className="inline-flex"
                      >
                        <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                      </motion.span>
                    )}
                  </AnimatePresence>
                </button>
              </div>
            </div>
          </div>
        </nav>
      </motion.header>

      {/* Mobile/Tablet backdrop overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="xl:hidden fixed inset-0 bg-primary-500/10 backdrop-blur-sm z-40"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      {/* Mobile/Tablet floating menu - Shows below xl (R-12) */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -16, scale: 0.98 }}
            transition={{ duration: 0.25, ease: EASING }}
            className="xl:hidden fixed top-20 left-4 right-4 md:left-6 md:right-6 rounded-xl bg-white border border-primary-500/10 shadow-[0_16px_48px_rgb(15_51_91/18)] z-50 max-h-[calc(100vh-6rem)] flex flex-col overflow-hidden"
          >
            <div className="overflow-y-auto">
            {/* Navigation Items */}
            <div className="px-3 pt-3 pb-2 space-y-4">
              {user && isAdminRole(user.role) ? (
                adminGroups.map((group) => {
                  const GroupIcon = ADMIN_GROUP_ICONS[group.key];
                  const groupActive = getGroupForPath(location.pathname)?.key === group.key;
                  const tileClass = (active: boolean) =>
                    [
                      'flex flex-col items-center justify-center p-4 rounded-lg text-sm font-medium transition-all min-h-[80px]',
                      active
                        ? 'bg-primary-500/5 border border-primary-500/15 text-primary-500'
                        : 'text-primary-500/70 hover:text-primary-500 hover:bg-primary-500/5 border border-transparent',
                    ].join(' ');

                  if (group.disabled) {
                    return (
                      <div
                        key={group.key}
                        className="flex flex-col items-center justify-center p-4 rounded-lg text-sm font-medium min-h-[80px] border border-neutral-200 text-neutral-600 cursor-not-allowed"
                        aria-disabled="true"
                        title={group.disabledReason || 'Coming soon'}
                      >
                        {GroupIcon && <GroupIcon className="w-6 h-6 mb-2" />}
                        <span className="text-center">{group.label}</span>
                        <span className="mt-1 text-xs text-neutral-600 text-center">
                          {group.disabledReason || 'Coming soon'}
                        </span>
                      </div>
                    );
                  }

                  if (group.tabs.length === 0) {
                    return (
                      <Link
                        key={group.key}
                        to={group.href || '#'}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={tileClass(groupActive)}
                      >
                        {GroupIcon && <GroupIcon className="w-6 h-6 mb-2" />}
                        <span className="text-center">{group.label}</span>
                      </Link>
                    );
                  }

                  return (
                    <div key={group.key} className="space-y-3 border-t border-primary-500/10 pt-4 first:border-t-0 first:pt-0">
                      <Link
                        to={group.href || '#'}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={[
                          'block px-2 text-xs font-semibold uppercase tracking-wider',
                          groupActive ? 'text-primary-500' : 'text-primary-500/50',
                        ].join(' ')}
                      >
                        {group.label}
                      </Link>
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                        {group.tabs.map((tab) => {
                          const TabIcon = ADMIN_TAB_ICONS[tab.key];
                          return (
                            <Link
                              key={tab.key}
                              to={tab.href}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className={tileClass(tab.match(location.pathname))}
                            >
                              {TabIcon && <TabIcon className="w-6 h-6 mb-2" />}
                              <span className="text-center">{tab.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              ) : (
                <>
              {navItems.filter(i => !i.isDropdown).length > 0 && (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {navItems.filter(i => !i.isDropdown).map((item) => {
                    const isDisabled = isNavItemDisabled(item);
                    if (isDisabled) {
                      return (
                        <div
                          key={item.name}
                          className="flex flex-col items-center justify-center p-4 rounded-lg text-sm font-medium min-h-[80px] border border-neutral-200 text-neutral-400 cursor-not-allowed"
                          aria-disabled="true"
                          title={item.disabledReason || 'Under construction'}
                        >
                          {item.icon && <item.icon className="w-6 h-6 mb-2" />}
                          <span className="text-center">{item.name}</span>
                          <span className="mt-1 text-xs text-neutral-400 text-center">
                            {item.disabledReason || 'Coming soon'}
                          </span>
                        </div>
                      );
                    }

                    return (
                      <Link
                        key={item.name}
                        to={item.href || '#'}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={[
                          'flex flex-col items-center justify-center p-4 rounded-lg text-sm font-medium transition-all min-h-[80px]',
                          isNavItemActive(item)
                            ? 'bg-primary-500/5 border border-primary-500/15 text-primary-500'
                            : 'text-primary-500/70 hover:text-primary-500 hover:bg-primary-500/5 border border-transparent'
                        ].join(' ')}
                      >
                        {item.icon && <item.icon className="w-6 h-6 mb-2" />}
                        <span className="text-center">{item.name}</span>
                      </Link>
                    );
                  })}
                </div>
              )}

              {navItems.filter(i => i.isDropdown).map(dropdown => (
                <div key={dropdown.name} className="space-y-3 border-t border-primary-500/10 pt-4">
                  <div className="px-2 text-xs font-semibold text-primary-500/50 uppercase tracking-wider">
                    {dropdown.name}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {dropdown.sections?.flatMap(sec => sec.items).map((item) => {
                      const isDisabled = isNavItemDisabled(item);
                      if (isDisabled) {
                        return (
                          <div
                            key={item.name}
                            className="flex flex-col items-center justify-center p-4 rounded-lg text-sm font-medium min-h-[80px] border border-neutral-200 text-neutral-400 cursor-not-allowed"
                            aria-disabled="true"
                            title={item.disabledReason || 'Under construction'}
                          >
                            {item.icon && <item.icon className="w-6 h-6 mb-2" />}
                            <span className="text-center">{item.name}</span>
                            <span className="mt-1 text-xs text-neutral-400 text-center">
                              {item.disabledReason || 'Coming soon'}
                            </span>
                          </div>
                        );
                      }

                      return (
                        <Link
                          key={item.name}
                          to={item.href || '#'}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={[
                            'flex flex-col items-center justify-center p-4 rounded-lg text-sm font-medium transition-all min-h-[80px]',
                            item.href && isActivePage(item.href)
                              ? 'bg-primary-500/5 border border-primary-500/15 text-primary-500'
                              : 'text-primary-500/70 hover:text-primary-500 hover:bg-primary-500/5 border border-transparent'
                          ].join(' ')}
                        >
                          {item.icon && <item.icon className="w-6 h-6 mb-2" />}
                          <span className="text-center">{item.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
                </>
              )}
            </div>

            {/* Public actions */}
            {!user && (
              <div className="px-3 pt-2 pb-4 border-t border-black/5">
                                  <Link
                    to="/apply"
                    className="w-full inline-flex items-center justify-center rounded-lg px-4 py-4 text-sm font-medium text-white bg-primary-500 hover:opacity-90 transition-opacity min-h-[44px]"
                  >
                  Apply
                </Link>
                                  <Link
                    to="/admin/login"
                    className="mt-2 w-full inline-flex items-center justify-center rounded-lg px-4 py-4 text-sm font-medium text-primary-500/80 hover:text-primary-500 hover:bg-primary-500/5 transition-colors min-h-[44px]"
                  >
                  Team Access
                </Link>
              </div>
            )}

            {/* Mobile user section */}
            {user && (
              <div className="pt-3 pb-4 border-t border-primary-500/10">
                <div className="px-4 flex items-center">
                  <UserAvatar user={user} size="md" showBorder={false} />
                  <div className="ml-3 min-w-0 flex-1">
                    <div className="text-base font-medium text-primary-500 truncate">
                      {user.displayName?.split(' ')[0] || user.email?.split('@')[0] || 'User'}
                    </div>
                    <div className="text-sm text-primary-500/60 truncate">{user.email}</div>
                  </div>
                </div>
                <div className="mt-4 px-3 space-y-1">
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate(isAdminRole(user.role) ? '/admin/settings' : '/settings');
                    }}
                    className="w-full flex items-center px-3 py-3 rounded-lg text-base font-medium text-primary-500/80 hover:text-primary-500 hover:bg-primary-500/5 transition-colors min-h-[44px]"
                  >
                    <Cog6ToothIcon className="w-5 h-5 mr-3" />
                    Settings
                  </button>
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/settings');
                    }}
                    className="w-full flex items-center px-3 py-3 rounded-lg text-base font-medium text-primary-500/80 hover:text-primary-500 hover:bg-primary-500/5 transition-colors min-h-[44px]"
                  >
                    <UserCircleIcon className="w-5 h-5 mr-3" />
                    Account
                  </button>
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      handleLogout();
                    }}
                    className="w-full flex items-center px-3 py-3 rounded-lg text-base font-medium text-error-500/80 hover:text-error-500 hover:bg-error-500/5 transition-colors min-h-[44px]"
                  >
                    <ArrowRightOnRectangleIcon className="w-5 h-5 mr-3" />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Spacer ensures page content is not hidden behind fixed header */}
      <div
        aria-hidden
        className={
          spacerSize === 'none'
            ? 'h-0'
            : spacerSize === 'sm'
            ? 'h-16 md:h-20'
            : spacerSize === 'lg'
            ? 'h-32 md:h-36'
            : 'h-20 md:h-[88px]'
        }
      />
    </>
  );
};

export default Navigation;